import React, { useState } from "react";
import {
  MapPin,
  Share2,
  ChevronDown,
  ChevronRight,
  Building2,
  Layers,
  Trees,
  Landmark,
  GraduationCap,
  HeartPulse,
  Briefcase,
  ShoppingBag,
  TrainFront,
  Compass,
  Sparkles,
  CalendarCheck,
  Home,
  BadgeCheck,
} from "lucide-react";

/**
 * SOBHA ONEWORLD — "One Residences" Project Detail Page
 * -------------------------------------------------------------
 * Drop your photography / renders into /public/images using the
 * filenames in the IMAGES map below. Every image renders through
 * the <Frame> component, so swapping a file is the only step
 * needed — no markup changes required.
 *
 * Install: npm install lucide-react
 * No custom fonts / font-family overrides are used — the page
 * relies on Tailwind's default system font stack.
 * -------------------------------------------------------------
 */

import Navbar from "../HomePage/Navbar";
import Footer from "../HomePage/Footer";

import hero from "./Images/s1.png";
import masterplan from "./Images/s2.png";
import legacy from "./Images/s3.png";
import garden from "./Images/s4.png";

const clubhouse = hero;
const pool = masterplan;
const sportsZone = legacy;
const locationMap = garden;
const towers = hero;
const towerAlpine = masterplan;
const towerBlossom = legacy;


// const IMAGES = {
//   hero: "/images/oneworld-hero.jpg",
//   masterplan: "/images/oneworld-masterplan.jpg",
//   legacy: "/images/oneworld-legacy.jpg",
//   locationMap: "/images/oneworld-location-map.jpg",
//   towers: "/images/oneworld-towers.jpg",
//   clubhouse: "/images/oneworld-clubhouse.jpg",
// };

const WHATSAPP_NUMBER = "918088687429";
const MAPS_URL = "https://maps.app.goo.gl/BzPiNd5T8T79iTRHA?g_st=ac";

const WHATSAPP_MESSAGE = `Hi, I'm interested in *SOBHA OneWorld — One Residences*.

📍 Location: ${MAPS_URL}
🏗 48 Acres · 14 Wings · 3,484 Units · Greater Whitefield, Bengaluru
📅 RERA Approval: 8-May-2026 · Launch: June-2026
💰 Launch Basic Rate: ₹13,999/sft

Could you share the configuration-wise price list and floor plans?`;

function buildWhatsAppLink() {
  return `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(
    WHATSAPP_MESSAGE
  )}`;
}

/* ---------- Shared bits ---------- */

function Frame({ src, alt, ratio = "aspect-[4/5]", className = "" }) {
  return (
    <div className={`relative overflow-hidden rounded-3xl bg-gray-100 ${ratio} ${className}`}>
      {/* eslint-disable-next-line jsx-a11y/alt-text */}
      <img
        src={src}
        alt={alt}
        onError={(e) => {
          e.currentTarget.style.display = "none";
          e.currentTarget.nextSibling.style.display = "flex";
        }}
        className="absolute inset-0 h-full w-full object-cover"
      />
      <div
        style={{ display: "none" }}
        className="absolute inset-0 flex items-center justify-center px-4 text-center text-xs font-medium uppercase tracking-wide text-gray-400"
      >
        {alt}
      </div>
    </div>
  );
}

function FloatingBadge({ className = "", children }) {
  return (
    <div className={`absolute rounded-2xl bg-white px-5 py-3 shadow-xl shadow-gray-300/40 ${className}`}>
      {children}
    </div>
  );
}

function NavPill({ children, active }) {
  return (
    <button
      className={`rounded-full px-5 py-2.5 text-sm font-semibold transition-colors ${
        active ? "bg-red-50 text-red-600" : "text-gray-700 hover:text-red-600"
      }`}
    >
      {children}
    </button>
  );
}

function ServiceIcon({ icon: Icon, label, isNew }) {
  return (
    <div className="flex flex-shrink-0 flex-col items-center gap-2 text-center">
      <div className="relative flex h-14 w-14 items-center justify-center rounded-2xl bg-white/15">
        {isNew && (
          <span className="absolute -top-2 right-0 rounded-full bg-yellow-400 px-1.5 py-0.5 text-[9px] font-bold uppercase text-red-700">
            New
          </span>
        )}
        <Icon className="h-6 w-6 text-white" strokeWidth={1.8} />
      </div>
      <span className="whitespace-nowrap text-sm font-semibold text-white">{label}</span>
    </div>
  );
}

function ChecklistItem({ icon: Icon, children }) {
  return (
    <div className="flex items-center gap-3">
      <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-red-50">
        <Icon className="h-5 w-5 text-red-600" strokeWidth={2} />
      </div>
      <span className="text-sm font-semibold text-gray-800 sm:text-base">{children}</span>
    </div>
  );
}

function JumpPill({ icon: Icon, label }) {
  return (
    <button className="flex items-center gap-2 rounded-full border border-gray-200 bg-white px-5 py-3 text-sm font-semibold text-gray-800 shadow-sm transition-colors hover:border-red-200 hover:text-red-600">
      <Icon className="h-4 w-4" strokeWidth={2} />
      {label}
      <ChevronRight className="h-3.5 w-3.5" />
    </button>
  );
}

function StatPill({ value, label }) {
  return (
    <div className="rounded-2xl bg-red-50 px-5 py-4 text-center">
      <p className="text-2xl font-extrabold text-red-600">{value}</p>
      <p className="mt-0.5 text-xs font-medium text-gray-500">{label}</p>
    </div>
  );
}

function ConnectivityGroup({ icon: Icon, title, rows }) {
  return (
    <div className="rounded-3xl border border-gray-100 bg-white p-6 shadow-sm">
      <div className="flex items-center gap-2.5">
        <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-red-50">
          <Icon className="h-4.5 w-4.5 text-red-600" strokeWidth={2} />
        </div>
        <h4 className="text-sm font-bold uppercase tracking-wide text-gray-900">{title}</h4>
      </div>
      <ul className="mt-4 space-y-3">
        {rows.map((r) => (
          <li key={r.name} className="flex items-baseline justify-between gap-4 border-b border-gray-100 pb-2.5">
            <span className="text-sm font-medium text-gray-700">{r.name}</span>
            <span className="whitespace-nowrap rounded-full bg-gray-50 px-2.5 py-0.5 text-xs font-bold text-red-500">
              {r.time}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function UpcomingCard({ icon: Icon, title, desc, time }) {
  return (
    <div className="rounded-3xl border border-gray-100 bg-white p-6 shadow-sm">
      <div className="flex items-center justify-between">
        <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-red-50">
          <Icon className="h-5 w-5 text-red-600" strokeWidth={1.8} />
        </div>
        <span className="rounded-full bg-gray-50 px-3 py-1 text-xs font-bold text-red-500">{time}</span>
      </div>
      <h4 className="mt-4 text-base font-extrabold text-gray-900">{title}</h4>
      <p className="mt-2 text-sm leading-relaxed text-gray-600">{desc}</p>
    </div>
  );
}

function ConfigCard({ type, area, price }) {
  return (
    <div className="rounded-3xl border border-gray-100 bg-white p-6 text-center shadow-sm transition-shadow hover:shadow-md">
      <p className="text-sm font-extrabold uppercase tracking-wide text-red-600">{type}</p>
      <p className="mt-3 text-2xl font-extrabold text-gray-900">{price}</p>
      <p className="mt-1 text-xs font-medium text-gray-500">{area}</p>
    </div>
  );
}

function MiniTable({ rows, leftLabel, rightLabel }) {
  return (
    <div className="overflow-hidden rounded-3xl border border-gray-100 bg-white shadow-sm">
      <div className="flex justify-between bg-gray-50 px-6 py-3">
        <span className="text-xs font-bold uppercase tracking-wide text-gray-500">{leftLabel}</span>
        <span className="text-xs font-bold uppercase tracking-wide text-gray-500">{rightLabel}</span>
      </div>
      <div>
        {rows.map(([a, b], i) => (
          <div
            key={a}
            className={`flex items-center justify-between px-6 py-3 text-sm ${
              i % 2 === 0 ? "bg-white" : "bg-gray-50/60"
            }`}
          >
            <span className="font-medium text-gray-700">{a}</span>
            <span className="font-bold text-red-600">{b}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* =====================================================================
   DATA
   ===================================================================== */

const CHECKLIST = [
  { icon: BadgeCheck, text: "RERA Approved · 8-May-2026" },
  { icon: Layers, text: "48 Acres, 14 Wings" },
  { icon: Home, text: "3,484 Total Units" },
  { icon: Trees, text: "1,20,000 sft Club House" },
];

const CONFIGS = [
  { type: "4 BHK Grande", area: "2,415 sq.ft.", price: "₹3.86 Cr" },
  { type: "4 BHK Luxe", area: "2,096 sq.ft.", price: "₹3.33 Cr" },
  { type: "3 BHK Grande", area: "1,735 – 1,838 sq.ft.", price: "₹2.81 Cr" },
  { type: "3 BHK Luxe", area: "1,510 sq.ft.", price: "₹2.34 Cr" },
  { type: "2 BHK Grande", area: "1,204 sq.ft.", price: "₹1.85 Cr" },
  { type: "2 BHK Luxe", area: "1,063 sq.ft.", price: "₹1.63 Cr" },
  { type: "1 BHK", area: "734 sq.ft.", price: "₹1.12 Cr" },
];

const PHASES = [
  ["Phase 1 — Wings 1 & 2", "Jul 2032"],
  ["Phase 6 — Wings 12, 13, 14", "Aug 2032"],
  ["Phase 2 — Wings 3 & 4", "Nov 2032"],
  ["Phase 5 — Wings 9, 10, 11", "Feb 2033"],
  ["Phase 3 — Wings 5 & 6", "Sep 2033"],
  ["Phase 4 — Wings 7 & 8", "Sep 2033"],
];

const FLOOR_RISE = [
  ["2nd – 4th Floor", "₹0 / sft"],
  ["5th – 9th Floor", "₹60 / sft"],
  ["10th – 14th Floor", "₹120 / sft"],
  ["15th – 19th Floor", "₹180 / sft"],
  ["20th – 24th Floor", "₹250 / sft"],
  ["25th – 29th Floor", "₹320 / sft"],
  ["30th – 34th Floor", "₹400 / sft"],
  ["35th – 39th Floor", "₹480 / sft"],
  ["40th – 47th Floor", "₹570 / sft"],
];

const PLC = [
  ["Select", "₹0"],
  ["Prime", "₹222 / sft"],
  ["Prime+", "₹444 / sft (4 BHK)"],
  ["Elite", "₹444 / sft"],
  ["Elite+", "₹666 / sft (4 BHK)"],
  ["Grande+", "₹888 / sft (4 BHK)"],
];

const CONNECTIVITY = [
  {
    icon: GraduationCap,
    title: "Education",
    rows: [
      { name: "Focus Academy Global & The Brigade School", time: "15 km" },
      { name: "Global Residential School", time: "13 km" },
      { name: "DPS Whitefield & Birla Open Minds Intl.", time: "10 km" },
      { name: "National Public, Eqube & Polaris Intl.", time: "8 km" },
      { name: "New Baldwin Intl. School", time: "6 km" },
      { name: "VIBGYOR High School", time: "2 km" },
    ],
  },
  {
    icon: HeartPulse,
    title: "Healthcare",
    rows: [
      { name: "Vydehi, Manipal ITPL & Aster Whitefield", time: "13 km" },
      { name: "East Point Hospital", time: "8 km" },
      { name: "MVJ Hospital", time: "6 km" },
      { name: "Motherhood Clinic", time: "4 km" },
    ],
  },
  {
    icon: Briefcase,
    title: "Corporates",
    rows: [
      { name: "KIADB Aerospace Park", time: "20 km" },
      { name: "Bagmane WTC, RMZ Infinity, EPIP", time: "15 km" },
      { name: "ITPL & GR Tech Park", time: "10 km" },
      { name: "Brigade Signature Towers", time: "4 km" },
      { name: "Bearys Global Research", time: "3 km" },
    ],
  },
  {
    icon: ShoppingBag,
    title: "Leisure & Conveniences",
    rows: [
      { name: "Gopalan Signature, VR Bengaluru & Phoenix Marketcity", time: "14 km" },
      { name: "Nexus Shantiniketan & Inorbit Mall", time: "13 km" },
      { name: "Park Square Mall", time: "11 km" },
      { name: "Decathlon", time: "4 km" },
      { name: "Orion Uptown Mall", time: "2 km" },
    ],
  },
];

const UPCOMING = [
  {
    icon: Compass,
    title: "Bengaluru Business Corridor",
    desc: "Integration of the Peripheral Ring Road with NICE Road — a 116 km bypass that eases travel across the city, with Phase 2 set to cut intercity time further.",
    time: "8 min away",
  },
  {
    icon: Landmark,
    title: "Satellite Town Ring Road",
    desc: "Connects 12 satellite towns — Devanahalli, Doddaballapura, Hoskote, Sarjapura & more — simplifying intra-city travel.",
    time: "10 min away",
  },
  {
    icon: TrainFront,
    title: "Bengaluru–Chennai Expressway",
    desc: "Scheduled for launch in early 2027, bringing Chennai within a 3-hour drive of the project.",
    time: "10 min away",
  },
  {
    icon: TrainFront,
    title: "43 km Purple Line Metro",
    desc: "A proposed 16 km stretch from KR Puram to Hoskote, with a station near the Hoskote Toll Gate at walkable distance.",
    time: "Walkable",
  },
];

/* =====================================================================
   PAGE
   ===================================================================== */

export default function SobhaOneWorldPage() {
  const [tableTab, setTableTab] = useState("rise");

  return (
    <div className="bg-white text-gray-900">
        <Navbar/>
      {/* Header */}
      {/* <header className="flex items-center justify-between gap-4 px-5 py-4 sm:px-10">
        <div className="flex items-center gap-1.5 text-xl font-extrabold">
          <Building2 className="h-6 w-6 text-red-600" />
          <span>
            SOBHA <span className="text-red-600">OneWorld</span>
          </span>
        </div>

        <nav className="hidden items-center gap-1 rounded-full border border-gray-100 bg-white px-2 py-1.5 shadow-sm lg:flex">
          <NavPill active>Overview</NavPill>
          <NavPill>Location</NavPill>
          <NavPill>Residences</NavPill>
          <NavPill>Pricing</NavPill>
          <NavPill>Connectivity</NavPill>
        </nav>

        <div className="flex items-center gap-3">
          <a
            href={MAPS_URL}
            target="_blank"
            rel="noreferrer"
            className="hidden items-center gap-2 rounded-full border border-red-200 px-5 py-2.5 text-sm font-bold text-red-600 transition-colors hover:bg-red-50 sm:flex"
          >
            <MapPin className="h-4 w-4" />
            Location
          </a>
          <a
            href={buildWhatsAppLink()}
            target="_blank"
            rel="noreferrer"
            className="flex items-center gap-2 rounded-full bg-green-500 px-5 py-2.5 text-sm font-bold text-white shadow-md shadow-green-200 transition-colors hover:bg-green-600"
          >
            <Share2 className="h-4 w-4" />
            WhatsApp
            <span className="h-2 w-2 rounded-full bg-white" />
          </a>
        </div>
      </header> */}

      {/* Services strip */}
      {/* <div className="overflow-x-auto bg-gradient-to-r from-red-600 to-red-500 px-5 py-6 sm:px-10">
        <div className="flex min-w-max items-start justify-center gap-10 sm:gap-14">
          <ServiceIcon icon={Compass} label="Overview" />
          <ServiceIcon icon={MapPin} label="Location" isNew />
          <ServiceIcon icon={Home} label="Residences" />
          <ServiceIcon icon={Layers} label="Configurations" />
          <ServiceIcon icon={CalendarCheck} label="Phases" />
          <ServiceIcon icon={Briefcase} label="Connectivity" />
          <ServiceIcon icon={Trees} label="Club House" isNew />
          <ServiceIcon icon={Share2} label="Share" />
        </div>
      </div> */}

      {/* Hero */}
      <section className="bg-gradient-to-b from-red-50/70 via-red-50/20 to-white px-5 py-14 sm:px-10 sm:py-20 -mt-24">
        <div className="mx-auto grid max-w-6xl gap-12 lg:grid-cols-2 lg:items-center">
          {/* Left */}
          <div>
            <p className="text-sm font-bold uppercase tracking-wide text-red-600">
              SOBHA Presents · Greater Whitefield
            </p>
            <h1 className="mt-3 text-5xl font-extrabold leading-[1.05] text-gray-900 sm:text-6xl">
              Sobha <span className="text-red-600">OneWorld</span>
              <br />
             
            </h1>
            <p className="mt-6 max-w-md text-base leading-relaxed text-gray-600">
              SOBHA OneWorld is a landmark mega community across 48 acres,
              presenting One Residences — high-rise towers oriented around a
              central yard, paired with retail avenues, landscaped grounds
              and a 1,20,000 sft clubhouse built for a holistic lifestyle.
            </p>

            <div className="mt-8 grid grid-cols-2 gap-5">
              {CHECKLIST.map((c) => (
                <ChecklistItem key={c.text} icon={c.icon}>
                  {c.text}
                </ChecklistItem>
              ))}
            </div>

            <div className="mt-10 flex flex-wrap gap-3">
              <JumpPill icon={Home} label="Residences" />
              <JumpPill icon={Layers} label="Configurations" />
              <JumpPill icon={CalendarCheck} label="Phases" />
              <JumpPill icon={MapPin} label="Connectivity" />
            </div>
          </div>

          {/* Right */}
          <div className="relative">
            <Frame src={hero} alt="SOBHA OneWorld — masterplan view" ratio="aspect-[4/3]" />
            <FloatingBadge className="-left-3 top-6 sm:-left-6">
              <p className="text-sm font-extrabold text-gray-900">Launch Basic Rate</p>
              <p className="text-xs text-gray-500">₹13,999 / sft</p>
            </FloatingBadge>
            <FloatingBadge className="-bottom-5 -right-3 sm:-right-6">
              <p className="text-xl font-extrabold text-red-600">~112 m</p>
              <p className="text-xs text-gray-500">Central Landscaped Yard</p>
            </FloatingBadge>
          </div>
        </div>
      </section>

      {/* Legacy / brand story */}
      <section className="px-5 py-16 sm:px-10 sm:py-20">
        <div className="mx-auto grid max-w-6xl gap-10 lg:grid-cols-2 lg:items-center">
          <Frame src={legacy} alt="SOBHA — three decades of craftsmanship" ratio="aspect-[4/3]" />
          <div>
            <p className="text-sm font-bold uppercase tracking-wide text-red-600">The Creators of Worlds</p>
            <h2 className="mt-2 text-3xl font-extrabold text-gray-900 sm:text-4xl">
              Three decades of building Bengaluru's benchmark.
            </h2>
            <p className="mt-5 text-sm leading-relaxed text-gray-600 sm:text-base">
              For over three decades, SOBHA has shaped a pan-India legacy
              built on its Backward Integration model — design, engineering
              and craftsmanship under one exacting standard. That philosophy
              now enters a future-ready chapter: a next-generation integrated
              community envisioned through life-centric design, with a
              grand wave-like canopy entrance and a lush green roundabout
              that elevate the arrival experience.
            </p>
          </div>
        </div>
      </section>

      {/* Stat strip */}
      <section className="px-5 sm:px-10">
        <div className="mx-auto grid max-w-6xl grid-cols-2 gap-4 sm:grid-cols-4">
          <StatPill value="48" label="Acres" />
          <StatPill value="14" label="Wings" />
          <StatPill value="3,484" label="Total Units" />
          <StatPill value="1.2L" label="Sqft Club House" />
        </div>
      </section>

      {/* Location */}
      <section className="px-5 py-16 sm:px-10 sm:py-20">
        <div className="mx-auto max-w-6xl">
          <div className="grid gap-12 lg:grid-cols-[1fr_1.1fr] lg:items-start">
            <div>
              <p className="text-sm font-bold uppercase tracking-wide text-red-600">Greater Whitefield</p>
              <h2 className="mt-2 text-3xl font-extrabold text-gray-900 sm:text-4xl">
                The city's eastern arc of tomorrow.
              </h2>
              <p className="mt-4 max-w-sm text-sm leading-relaxed text-gray-600">
                As Bengaluru's latest lifestyle corridor, this peri-urban area
                brings future-ready infrastructure, key city routes and
                employment hubs together. As an extension of Whitefield, it
                stays well linked to KR Puram and the airport, with schools,
                healthcare, retail and lifestyle essentials nearby.
              </p>
              <a
                href={MAPS_URL}
                target="_blank"
                rel="noreferrer"
                className="mt-6 inline-flex items-center gap-2 rounded-full bg-red-600 px-6 py-3.5 text-sm font-bold text-white shadow-md shadow-red-200 transition-colors hover:bg-red-700"
              >
                <MapPin className="h-4 w-4" />
                Open in Google Maps
              </a>
            </div>
            <Frame src={locationMap} alt="Greater Whitefield location map" ratio="aspect-[4/3]" />
          </div>

          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {CONNECTIVITY.map((c) => (
              <ConnectivityGroup key={c.title} {...c} />
            ))}
          </div>
        </div>
      </section>

      {/* Upcoming developments */}
      <section className="bg-gray-50 px-5 py-16 sm:px-10 sm:py-20">
        <div className="mx-auto max-w-6xl">
          <p className="text-sm font-bold uppercase tracking-wide text-red-600">The Future-Ready Horizon</p>
          <h2 className="mt-2 max-w-lg text-3xl font-extrabold text-gray-900 sm:text-4xl">
            Key upcoming developments nearby.
          </h2>
          <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {UPCOMING.map((u) => (
              <UpcomingCard key={u.title} {...u} />
            ))}
          </div>
        </div>
      </section>

      {/* One Residences / Towers */}
      <section className="px-5 py-16 sm:px-10 sm:py-20">
        <div className="mx-auto max-w-6xl">
          <div className="grid gap-12 lg:grid-cols-2 lg:items-center">
            <div>
              <p className="text-sm font-bold uppercase tracking-wide text-red-600">One Residences</p>
              <h2 className="mt-2 text-3xl font-extrabold text-gray-900 sm:text-4xl">A first of its kind.</h2>
              <p className="mt-4 text-sm leading-relaxed text-gray-600 sm:text-base">
                Contemporary high-rise towers gather around a Central Yard,
                giving every residence eternal vistas of the landscape.
                Abundant natural light and flowing cross-ventilation infuse
                the homes with sensibility from end to end — crafted for
                those who seek a holistic lifestyle.
              </p>
              <div className="mt-8 space-y-3">
                <div className="rounded-2xl border border-gray-100 bg-white p-5 shadow-sm">
                  <p className="text-sm font-bold text-gray-900">Wings 1, 2, 9, 10, 11, 12, 13, 14</p>
                  <p className="mt-1 text-sm text-gray-600">3 Basements + Ground + Parking + 46 Floors</p>
                </div>
                <div className="rounded-2xl border border-gray-100 bg-white p-5 shadow-sm">
                  <p className="text-sm font-bold text-gray-900">Wings 3, 4, 5, 6, 7, 8</p>
                  <p className="mt-1 text-sm text-gray-600">3 Basements + Ground + Parking + 45 Floors</p>
                </div>
                <div className="rounded-2xl border border-red-100 bg-red-50 p-5">
                  <p className="text-sm font-semibold text-red-700">
                    19th, 29th & 39th floors are designated refuge floors.
                  </p>
                </div>
              </div>
            </div>
            <Frame src={towers} alt="One Residences towers around the Central Yard" ratio="aspect-[4/3]" />
          </div>
        </div>
      </section>

      {/* Configurations & Pricing */}
      <section className="bg-gray-50 px-5 py-16 sm:px-10 sm:py-20">
        <div className="mx-auto max-w-6xl">
          <p className="text-sm font-bold uppercase tracking-wide text-red-600">Configurations & Average Cost</p>
          <h2 className="mt-2 text-3xl font-extrabold text-gray-900 sm:text-4xl">
            A residence for every way of living.
          </h2>
          <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {CONFIGS.map((c) => (
              <ConfigCard key={c.type} {...c} />
            ))}
          </div>
        </div>
      </section>

      {/* Phases */}
      <section className="px-5 py-16 sm:px-10 sm:py-20">
        <div className="mx-auto max-w-4xl">
          <p className="text-sm font-bold uppercase tracking-wide text-red-600">Construction Timeline</p>
          <h2 className="mt-2 text-3xl font-extrabold text-gray-900 sm:text-4xl">Phases & completion dates.</h2>
          <div className="mt-10">
            <MiniTable rows={PHASES} leftLabel="Phase & Wings" rightLabel="Completion" />
          </div>
        </div>
      </section>

      {/* Floor rise & PLC charges */}
      <section className="bg-gray-50 px-5 py-16 sm:px-10 sm:py-20">
        <div className="mx-auto max-w-5xl">
          <p className="text-sm font-bold uppercase tracking-wide text-red-600">Charges</p>
          <h2 className="mt-2 text-3xl font-extrabold text-gray-900 sm:text-4xl">
            Floor rise & preferred location charges.
          </h2>

          <div className="mt-8 inline-flex rounded-full border border-gray-200 bg-white p-1.5 shadow-sm">
            <button
              onClick={() => setTableTab("rise")}
              className={`rounded-full px-5 py-2 text-sm font-semibold transition-colors ${
                tableTab === "rise" ? "bg-red-600 text-white" : "text-gray-600"
              }`}
            >
              Floor Rise
            </button>
            <button
              onClick={() => setTableTab("plc")}
              className={`rounded-full px-5 py-2 text-sm font-semibold transition-colors ${
                tableTab === "plc" ? "bg-red-600 text-white" : "text-gray-600"
              }`}
            >
              Preferred Location
            </button>
          </div>

          <div className="mt-6">
            {tableTab === "rise" ? (
              <MiniTable rows={FLOOR_RISE} leftLabel="Floor Range" rightLabel="Charge" />
            ) : (
              <MiniTable rows={PLC} leftLabel="Category" rightLabel="Charge" />
            )}
          </div>
        </div>
      </section>

      {/* Club House strip */}
      <section className="px-5 sm:px-10">
        <div className="mx-auto max-w-6xl">
          <Frame src={clubhouse} alt="1,20,000 sft club house in 2.4 acres" ratio="aspect-[16/7]" />
        </div>
      </section>

      {/* Closing CTA */}
      <section className="mt-16 bg-gradient-to-r from-red-600 to-red-500 px-5 py-16 text-center sm:px-10 sm:py-20">
        <Sparkles className="mx-auto h-7 w-7 text-white" strokeWidth={1.6} />
        <h2 className="mx-auto mt-4 max-w-xl text-3xl font-extrabold text-white sm:text-4xl">
          A World Within Bengaluru.
        </h2>
        <p className="mx-auto mt-3 max-w-md text-sm text-red-50">
          Get configuration-wise pricing, the masterplan & a site visit slot on WhatsApp.
        </p>
        <div className="mt-7 flex flex-wrap justify-center gap-3">
          <a
            href="https://wa.me/message/MDG2ZSZNG6P6G1"
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 rounded-full bg-white px-7 py-3.5 text-sm font-bold text-red-600 shadow-lg transition-colors hover:bg-red-50"
          >
            Contact & Enquire
          </a>
          <a
            href={buildWhatsAppLink()}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 rounded-full bg-red-700 px-7 py-3.5 text-sm font-bold text-white shadow-lg transition-colors hover:bg-red-800"
          >
            <Share2 className="h-4 w-4" />
            Share on WhatsApp
          </a>
        </div>
        <p className="mt-4 text-xs font-semibold text-red-100">+91 80886 87429</p>
      </section>

      <footer className="px-5 py-8 text-center sm:px-10">
        <p className="text-xs font-medium uppercase tracking-wide text-gray-400">
          SOBHA OneWorld · Greater Whitefield, Bengaluru
        </p>
      </footer>

      {/* Floating WhatsApp button */}
      <a
        href={buildWhatsAppLink()}
        target="_blank"
        rel="noreferrer"
        aria-label="Share project details on WhatsApp"
        className="fixed bottom-6 right-5 z-50 flex h-14 w-14 items-center justify-center rounded-full bg-green-500 text-white shadow-xl shadow-green-300/50 transition-transform hover:scale-105 sm:bottom-8 sm:right-8"
      >
        <Share2 className="h-5 w-5" />
      </a>
      <Footer/>
    </div>
  );
}